<!--
  One site-visit row. Everything rendered here is derived from the ported transition table via the
  component's helpers — no state, label or group is listed in this template.

  🔴 The inline action does NOT perform the transition. It emits, and the surface hosting the row opens
  it: the opened row fetches the visit's detail and hands it to <site-visit-negotiation>, the same
  component the per-project area uses, which already carries every state's interaction. Nothing here
  re-implements one: two surfaces performing the same transitions is exactly where a silent divergence
  starts — and this component now IS two surfaces, so that is a rule rather than a preference.
-->
<article
  class="queue-row"
  [id]="anchorId"
  [class.queue-row--open]="open"
  [attr.data-testid]="vm.testId + '-row'"
>
  <div class="queue-row__main">
    <div class="queue-row__identity">
      <span
        class="pill queue-row__status"
        [ngClass]="pillClass(vm.row)"
        [attr.data-testid]="vm.testId + '-status'"
      >
        <span class="material-icons" aria-hidden="true">event</span>
        {{ label(vm.row) }}
      </span>

      <div class="queue-row__where">
        @if (vm.row.projectOrderId) {
        <span class="queue-row__project" [attr.data-testid]="vm.testId + '-project'">
          {{ vm.row.projectOrderId }}
        </span>
        } @if (siteText(vm.row)) {
        <span class="queue-row__site" [attr.data-testid]="vm.testId + '-site'">
          {{ siteText(vm.row) }}
        </span>
        } @if (vm.row.propertyName) {
        <span class="queue-row__property">{{ vm.row.propertyName }}</span>
        }
      </div>

      @if (vm.row.address) {
      <div class="queue-row__address" [attr.data-testid]="vm.testId + '-address'">
        {{ vm.row.address }}
      </div>
      }

      <!--
        The one operative fact: the day on the table, plus the arrival time when one has been
        proposed or confirmed. 🕐 The time string arrives rendered — it is printed, never
        re-formatted.
      -->
      @if (dayText(vm.row) || timeText(vm.row)) {
      <div class="queue-row__when" [attr.data-testid]="vm.testId + '-when'">
        <span class="material-icons" aria-hidden="true">schedule</span>
        @if (dayText(vm.row)) {<span>{{ dayText(vm.row) }}</span>} @if
        (timeText(vm.row)) {<span class="queue-row__time">· {{ timeText(vm.row) }}</span>}
      </div>
      }

      <!--
        🔴 Site-contact details render if and only if `parties` ARRIVED. connect-service builds it
        through the detail view's own reveal gate — non-null at TIME_CONFIRMED and nowhere else —
        so there is one gate for every surface. No status test here: a second one could come to
        disagree with the first, and a cross-project queue is the single surface most likely to
        become the one place a contact leaks mid-negotiation.

        The guard is on the FIELDS, not on the object, and matches
        `site-visit-negotiation.component.html` exactly: `partiesFor` answers a non-null record with
        null fields for a confirmed visit that has no stored site contact, and that is a real
        negotiation rather than a defensive case — `SiteVisitOutreachHandler` carries a dedicated
        PROJECT_MANAGER_NO_SITE_CONTACT_ADDRESS audience for it. Guarding the object would print a
        "Site contact" label with nothing after it here while the project view printed nothing at
        all, which is the divergence §5.1's *supplement* choice exists to forbid. This is not a
        second reveal gate — the reveal is still whatever connect-service put in `parties`.
      -->
      @if (vm.row.parties?.siteContactName || vm.row.parties?.siteContactPhone) {
      <div class="queue-row__contact" [attr.data-testid]="vm.testId + '-contact'">
        <span class="material-icons" aria-hidden="true">call</span>
        <span class="queue-row__contact-label">Site contact</span>
        <span>{{ vm.row.parties?.siteContactName }}</span>
        @if (vm.row.parties?.siteContactPhone) {
        <span class="queue-row__contact-phone">{{ vm.row.parties?.siteContactPhone }}</span>
        }
      </div>
      }
    </div>

    <!--
      🔴 `readOnly` gates the whole control block, so a view-only surface gets neither the pending
      action NOR *View Details*. Both open the negotiation panel, and that panel carries the
      transitions — gating only the action would leave the write path one click away behind a button
      that looks inert.
    -->
    @if (!readOnly) {
    <div class="queue-row__actions">
      @if (vm.settled) {
      <span class="queue-row__settled" [attr.data-testid]="vm.testId + '-settled'">
        Updated — this visit leaves the queue
      </span>
      } @else { @if (!open && vm.pending.length > 0) {
      <!--
        ONE inline action, whatever the pending set holds. Every one of these clicks does the same
        thing — it opens the row — so more than one button would be distinct labels and distinct test
        ids over identical behavior, promising a choice that is not made here. At
        `PENDING_PA_APPROVAL` the choice is real (accept the site contact's counter, or counter back
        with a different day and time) but it is made one click in, in the negotiation panel that
        already carries both. The label follows suit: the pending move's own words where there is
        exactly one, and words for opening the panel where there is more than one.

        🔴 Closed rows only. This button is a LABELLED SHORTCUT - it names the pending move and opens
        the panel - and that reading holds only while the panel is shut. Once it is open the panel is
        already showing the control that performs the move, so a second button beside it, naming the
        same transition but unable to perform it without whatever the panel's own form collects
        (a day, a time, a reason), would be one live verb and one inert copy of it. The toggle stays,
        so an open row carries exactly one control - *Close* - and every transition is reached where
        it is actually performed.
      -->
      <button
        type="button"
        class="btn btn--primary queue-row__action"
        [attr.data-testid]="actionTestId(vm)"
        (click)="action.emit(vm)"
      >
        {{ actionLabel(vm) }}
      </button>
      }

      <!--
        RESCHEDULE — move an already-agreed day. Excluded from the pending set on purpose, because it is
        legal from `TIME_CONFIRMED` (and from three retired, orphan-only states kept so a stale row can
        still be moved) and treating it as work owed would sweep almost every healthy visit into
        *Awaiting you*. Which means without a control of its own it appears nowhere: a confirmed visit's
        row would offer only *View Details*, and the PA who needs to move a date has no reason to believe
        details is where that lives. Until this existed the only way to shift an agreed day was to
        cancel — terminal, and the unique index means that vendor and site can never be scheduled again.
        Making the escape hatch discoverable is the whole point of the change.

        🔴 `showReschedule` (not `canReschedule` alone) is what gates this, on the general principle that
        a Reschedule button beside a row's own pending-action button would be a SECOND control doing the
        IDENTICAL thing — opening this same panel — which the rule above forbids for the pending move,
        not licenses as an exception to it. `showReschedule` suppresses the row's own control whenever
        `vm.pending.length > 0`; no reachable state hits that today (see the `.ts` doc comment), so this
        renders directly at every state `canReschedule` allows, but the guard stays for the state that
        reintroduces the overlap rather than assuming today's transition table is permanent.

        🔴 It emits its OWN `reschedule` output, not `action`, and that is the one thing about this
        button a refactor must not "simplify" back. It still only opens the row — the panel's reschedule
        form is what performs the move — but a PA who pressed a button named *Reschedule* has said which
        form they want, and the host can only honour that if it can tell this click apart from *View
        Details* and from the pending action. While both emitted `action` it could not: every route in
        looked identical, so the panel opened generically and the reschedule form sat collapsed behind
        its "Need to move the agreed day?" disclosure — a second click for a form already asked for by
        name. `(action)`/`(toggle)` are untouched; the hosts add one listener each. Closed rows only,
        for the reason the pending button is — once the panel is up it carries the control that actually
        works.
      -->
      @if (!open && showReschedule(vm)) {
      <button
        type="button"
        class="btn btn--secondary queue-row__reschedule"
        [attr.data-testid]="rescheduleTestId(vm)"
        (click)="reschedule.emit(vm)"
      >
        {{ rescheduleLabel }}
      </button>
      } }

      <button
        type="button"
        class="btn btn--secondary queue-row__open"
        [attr.aria-expanded]="open"
        [attr.data-testid]="vm.testId + '-open'"
        (click)="toggle.emit(vm)"
      >
        {{ open ? 'Close' : 'View Details' }}
      </button>
    </div>
    }
  </div>

  <!--
    The opened row's detail, projected by whichever surface hosts the row. It is deliberately NOT
    rendered here: the detail is fetched per visit and handed to <site-visit-negotiation>, and the
    fetch is what heals a parked visit — that belongs to the surface that owns the data, not to a
    presentational row. Hosts must wrap their projected detail in their own `@if`, so the panel is
    built only for the open row rather than for every row in the list.
  -->
  <ng-content></ng-content>
</article>
